import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpEvent } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ProvisioningJobCreateRequest, ProvisioningJobResponse } from '../models';

@Injectable({ providedIn: 'root' })
export class VmProvisionApiService {
  private readonly httpClient = inject(HttpClient);

  buildCustomerBaseUrl(customerId?: string | number | null): string {
    const targetCustomer = customerId ? encodeURIComponent(String(customerId)) : 'me';
    return `/api/customers/${targetCustomer}/provisioning`;
  }

  startJob(
    request: ProvisioningJobCreateRequest,
    customerId?: string | number | null,
  ): Observable<ProvisioningJobResponse> {
    const url = `${this.buildCustomerBaseUrl(customerId)}/jobs`;
    return this.httpClient.post<ProvisioningJobResponse>(url, request);
  }

  getJobStatus(jobId: string, customerId?: string | number | null): Observable<ProvisioningJobResponse> {
    const url = `${this.buildCustomerBaseUrl(customerId)}/jobs/${jobId}`;
    return this.httpClient.get<ProvisioningJobResponse>(url);
  }

  downloadImage(downloadUrl: string): Observable<HttpEvent<Blob>> {
    return this.httpClient.get(downloadUrl, {
      reportProgress: true,
      observe: 'events',
      responseType: 'blob',
    });
  }

  async fetchProgressStream(
    baseUrl: string,
    jobId: string,
    token: string | null | undefined,
    signal: AbortSignal,
  ): Promise<Response> {
    const headers: Record<string, string> = {
      Accept: 'text/event-stream',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return fetch(`${baseUrl}/jobs/${jobId}/progress`, {
      headers,
      signal,
    });
  }

  parseSha256Digest(headerValue: string | null | undefined): string | null {
    if (!headerValue || typeof headerValue !== 'string') {
      return null;
    }
    const trimmed = headerValue.trim();
    return this.extractRfcDigest(trimmed) || this.extractRfc3230Digest(trimmed) || this.extractDirectHex(trimmed);
  }

  private extractRfcDigest(header: string): string | null {
    const match = header.match(/sha-256\s*=\s*:([^:]+):/i);
    return match && match[1] ? this.base64ToHex(match[1].trim()) : null;
  }

  private extractRfc3230Digest(header: string): string | null {
    const match = header.match(/sha[-_]?256\s*=\s*([A-Za-z0-9+/=_-]+)/i);
    if (!match || !match[1]) return null;
    const val = match[1].trim();
    return /^[0-9a-fA-F]{64}$/.test(val) ? val.toLowerCase() : this.base64ToHex(val);
  }

  private extractDirectHex(header: string): string | null {
    return /^[0-9a-fA-F]{64}$/.test(header) ? header.toLowerCase() : null;
  }

  private base64ToHex(base64Str: string): string | null {
    try {
      const binary = atob(base64Str);
      let hex = '';
      for (let i = 0; i < binary.length; i++) {
        const byte = binary.charCodeAt(i).toString(16).padStart(2, '0');
        hex += byte;
      }
      return hex.toLowerCase();
    } catch {
      return null;
    }
  }

  parseFileName(contentDisposition: string | null | undefined): string | null {
    if (!contentDisposition) return null;
    return this.extractUtf8FileName(contentDisposition) || this.extractStandardFileName(contentDisposition);
  }

  private extractUtf8FileName(header: string): string | null {
    const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
    if (!utf8Match || !utf8Match[1]) return null;
    try {
      return this.sanitizeFileName(decodeURIComponent(utf8Match[1]));
    } catch {
      return this.sanitizeFileName(utf8Match[1]);
    }
  }

  private extractStandardFileName(header: string): string | null {
    const match = header.match(/filename="?([^";]+)"?/i);
    return match && match[1] ? this.sanitizeFileName(match[1].trim()) : null;
  }

  private sanitizeFileName(fileName: string): string | null {
    const withoutPath = fileName.replace(/^.*[\\/]/, '');
    const sanitized = withoutPath
      // eslint-disable-next-line no-control-regex
      .replace(/[\x00-\x1f\x7f]/g, '')
      .replace(/\.\.+/g, '.')
      .trim();
    return sanitized || null;
  }
}
